/**
 * Optimized Multi-Tier Frontend Gift Manager - JavaScript for Shopify Theme
 *
 * Performance optimized version:
 * - Minimal API calls with smart caching
 * - Proper cart UI synchronization
 * - Efficient event handling
 * - Batch operations for cart updates
 */

class OptimizedShopifyGiftManager {
  constructor(config = {}) {
    // Define gift tiers
    this.giftTiers = config.giftTiers || [
      {
        threshold: 130,
        items: [{ variantId: "42000124936286", quantity: 1 }],
        name: "1x Mystery Gift",
        value: "$50",
      },
      {
        threshold: 200,
        items: [{ variantId: "42000124969054", quantity: 1 }],
        name: "1x Mystery Gift",
        value: "$100",
      },
    ];

    this.giftTiers.sort((a, b) => a.threshold - b.threshold);

    // State management
    this.isProcessing = false;
    this.debounceTimeout = null;
    this.lastProcessedSubtotal = null;
    this.isCartDrawerOpen = false;

    // Cart caching to reduce API calls
    this.cartCache = null;
    this.cacheExpiry = 0;
    this.cacheValidityMs = 2000; // 2 second cache

    // Simple failure tracking
    this.failureCount = 0;
    this.maxFailures = 3;

    // Pre-compute gift variant IDs
    this.allGiftVariantIds = new Set(
      this.giftTiers.flatMap((tier) =>
        tier.items.map((item) => item.variantId),
      ),
    );

    // Flag to prevent self-triggering
    this.internalOperation = false;

    // Gift progress state
    this.lastProgressMessage = null;

    // Throttling variables to prevent excessive API calls
    this.lastQuickCartReload = null;
    this.lastCartRefresh = null;
    this.lastCartDrawerFetch = null;
    this.lastMonitorTime = 0;

    // Initialization tracking for checkout button management
    this.isInitialized = false;

    // Session storage key for tracking manually removed gifts
    this.sessionStorageKey = "giftManager_removedGifts";

    // Track last known tier to detect tier upgrades
    this.lastKnownTier = null;

    this.init();
    this.monitorCartUpdates();
  }

  init() {
    this.setupEventListeners();

    // Initial check with reasonable delay
    setTimeout(() => {
      this.checkAndManageGifts();
    }, 2000);

    // CRITICAL: Add fallback cart monitoring in case events don't fire
    this.setupFallbackCartMonitoring();
  }

  setupEventListeners() {
    console.log("🔧 Setting up gift manager event listeners...");

    // Listen for more cart events to catch all changes
    const cartEvents = [
      "cart:updated",
      "cart:changed",
      "cart:refresh",
      "cart:item-added",
    ];
    cartEvents.forEach((event) => {
      document.addEventListener(event, (e) => {
        console.log(`🔔 Cart event detected: ${event}`, e.detail);
        if (!this.internalOperation && e.detail?.source !== "giftManager") {
          console.log("✅ Triggering gift check from cart event");
          this.invalidateCache();
          this.scheduleCheck("cart-event");
        } else {
          console.log(
            "⏭️ Skipping gift check (internal operation or from gift manager)",
          );
        }
      });
    });

    // Stiletto theme-specific quantity events
    const stilettoEvents = [
      "quantity-update:add",
      "quantity-update:subtract",
      "quantity-update:remove",
    ];
    stilettoEvents.forEach((event) => {
      document.addEventListener(event, (e) => {
        console.log(`🔔 Stiletto event detected: ${event}`, e.detail);
        if (!this.internalOperation) {
          console.log("✅ Triggering gift check from Stiletto event");
          this.invalidateCache();

          // If cart drawer is open, use immediate check
          if (this.isCartDrawerOpen) {
            setTimeout(() => this.checkAndManageGifts(), 200);
          } else {
            // Delay to let cart update complete first
            setTimeout(() => this.scheduleCheck("quantity"), 500);
          }
        } else {
          console.log("⏭️ Skipping Stiletto event (internal operation)");
        }
      });
    });

    // Product quantity updates
    document.addEventListener("product:quantity-update", (e) => {
      if (!this.internalOperation) {
        this.invalidateCache();
        this.scheduleCheck("quantity");
      }
    });

    // Generic quantity input changes
    document.addEventListener("change", (e) => {
      if (
        e.target.matches(
          '.quantity-input__input, input[name*="quantity"], .qty, .quantity, [name*="updates"]',
        ) &&
        !this.internalOperation
      ) {
        this.invalidateCache();
        this.scheduleCheck("quantity");
      }
    });

    // Listen for specific Stiletto quantity buttons
    document.addEventListener("click", (e) => {
      if (
        e.target.matches("[data-input-item], .quantity-btn, [data-quantity]") &&
        !this.internalOperation
      ) {
        this.invalidateCache();
        // Delay for theme to process the update
        setTimeout(() => this.scheduleCheck("quantity"), 300);
      }

      // CRITICAL: Listen for remove/delete buttons (immediate check)
      if (
        e.target.matches(
          '[data-quantity="0"], .remove, .delete, [data-remove], .cart__remove',
        ) &&
        !this.internalOperation
      ) {
        this.invalidateCache();
        // Immediate check for removals
        this.scheduleCheck("removal");
      }
    });

    // Cart form submissions
    document.addEventListener("submit", (e) => {
      if (
        e.target.matches(
          '.quick-cart__form, .cart__form, form[action*="/cart"]',
        ) &&
        !this.internalOperation
      ) {
        this.invalidateCache();
        this.scheduleCheck();
      }
    });

    // CRITICAL: Pre-checkout validation
    document.addEventListener("click", (e) => {
      if (
        e.target.matches(
          '[name="add"], .checkout, .btn-checkout, [data-checkout], .cart__checkout, .cart__submit, .quick-cart__submit, [name="checkout"]',
        ) &&
        !this.internalOperation
      ) {
        e.preventDefault(); // Prevent immediate checkout

        console.log("🔎 Checkout button clicked - starting validation");
        this.invalidateCache();
        this.validateGiftsBeforeCheckout(e.target);
      }
    });

    // Listen for cart drawer/popup opening (when gifts might become visible)
    document.addEventListener("quick-cart:open", (e) => {
      if (!this.internalOperation) {
        this.isCartDrawerOpen = true;
        setTimeout(() => {
          this.checkAndManageGifts();
          this.startRealTimeCartMonitoring();
          this.startCartDrawerMonitoring();
        }, 100);
      }
    });

    // Listen for cart drawer closing
    document.addEventListener("quick-cart:close", (e) => {
      this.isCartDrawerOpen = false;
      this.stopCartDrawerMonitoring();
    });

    // Also monitor cart page loads
    if (window.location.pathname.includes("/cart")) {
      setTimeout(() => {
        this.ensureFreeShippingBarWorks();
        this.checkAndManageGifts();
        this.startRealTimeCartMonitoring();
      }, 1000);
    }

    // Initial free shipping bar check for all pages
    setTimeout(() => {
      this.ensureFreeShippingBarWorks();
      this.cleanupStandaloneElements();
    }, 500);
  }

  monitorCartUpdates() {
    // Debounce monitoring to prevent cascading events
    this.monitoringDebounce = null;
    this.lastMonitorTime = 0;

    // Method 1: Monitor cart object changes if available (most reliable)
    setTimeout(() => {
      if (typeof cart !== "undefined" && cart.updateItem) {
        const originalUpdateItem = cart.updateItem;
        cart.updateItem = (...args) => {
          const result = originalUpdateItem.apply(cart, args);

          if (!this.internalOperation) {
            this.invalidateCache();
            this.debouncedMonitoringCheck("updateItem", 800);
          }

          return result;
        };
      }
    }, 2000);

    // Method 2: Light DOM monitoring (only for specific changes)
    const cartObserver = new MutationObserver((mutations) => {
      if (this.internalOperation) return;

      // Check for meaningful cart changes only
      let quantityChanged = false;
      mutations.forEach((mutation) => {
        // Only watch for quantity input value changes or cart count updates
        if (
          mutation.type === "attributes" &&
          (mutation.attributeName === "value" ||
            mutation.attributeName === "data-cart-count")
        ) {
          quantityChanged = true;
        } else if (
          mutation.type === "childList" &&
          mutation.target.matches &&
          mutation.target.matches(".cart__total, .quick-cart__footer-subtotal")
        ) {
          quantityChanged = true;
        }
      });

      if (quantityChanged) {
        this.invalidateCache();
        this.debouncedMonitoringCheck("dom", 800);
      }
    });

    // Start observing only specific elements
    setTimeout(() => {
      const observeElements = document.querySelectorAll(
        ".quantity-input__input, [data-cart-count], .cart__total, .quick-cart__footer-subtotal",
      );
      observeElements.forEach((element) => {
        cartObserver.observe(element, {
          attributes: true,
          attributeFilter: ["value", "data-cart-count"],
          childList: true,
          subtree: false, // Don't observe deep children
        });
      });
    }, 1000);

    // Method 3: Reduced frequency fallback monitoring with proper timeouts
    this.lastKnownSubtotal = null;
    this.subtotalCheckInterval = setInterval(() => {
      if (this.isProcessing || this.internalOperation) return;

      // Use cached data if available to avoid API calls
      if (this.cartCache && Date.now() < this.cacheExpiry) {
        const currentSubtotal = this.calculateSubtotal(this.cartCache);

        if (
          this.lastKnownSubtotal !== null &&
          Math.abs(currentSubtotal - this.lastKnownSubtotal) >= 1
        ) {
          this.debouncedMonitoringCheck("subtotal", 0);
        }

        this.lastKnownSubtotal = currentSubtotal;
      }
    }, 10000); // Every 10 seconds with proper throttling
  }

  startRealTimeCartMonitoring() {
    if (this.realTimeMonitor) {
      return; // Already monitoring
    }

    // Monitor quantity inputs in real-time (reduced frequency)
    this.realTimeMonitor = setInterval(() => {
      if (this.internalOperation) return;

      this.monitorQuantityInputs();
      this.monitorCartTotals();
    }, 3000); // Check every 3 seconds with throttling

    // Also set up DOM watching
    this.setupAggressiveDOMWatching();
  }

  monitorQuantityInputs() {
    const quantityInputs = document.querySelectorAll(
      '.quantity-input__input, input[name*="quantity"]',
    );

    quantityInputs.forEach((input) => {
      const currentValue = parseInt(input.value) || 0;
      const lastValue = parseInt(input.dataset.lastGiftCheckValue) || 0;

      if (currentValue !== lastValue) {
        input.dataset.lastGiftCheckValue = currentValue;

        // Force cache invalidation for quantity changes
        this.invalidateCache();

        // If quantity increased, trigger a faster check
        if (currentValue > lastValue) {
          this.scheduleCheck("realtime-quantity");
        } else {
          this.scheduleCheck("removal");
        }
      }
    });
  }

  monitorCartTotals() {
    const totalElements = document.querySelectorAll(
      ".cart__total, .quick-cart__footer-subtotal, [data-cart-total]",
    );

    totalElements.forEach((element) => {
      const currentText = element.textContent.trim();
      const lastText = element.dataset.lastGiftCheckTotal || "";

      if (currentText !== lastText && currentText.includes("$")) {
        element.dataset.lastGiftCheckTotal = currentText;
        this.invalidateCache();
        this.scheduleCheck("realtime-total");
      }
    });
  }

  setupAggressiveDOMWatching() {
    // Watch for any cart item being removed from DOM
    const cartItemObserver = new MutationObserver((mutations) => {
      if (this.internalOperation) return;

      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          // Check if cart items were removed
          mutation.removedNodes.forEach((node) => {
            if (
              node.nodeType === Node.ELEMENT_NODE &&
              ((node.matches &&
                node.matches(
                  ".cart-item, .quick-cart__item, [data-cart-item]",
                )) ||
                (node.querySelector &&
                  node.querySelector(
                    ".cart-item, .quick-cart__item, [data-cart-item]",
                  )))
            ) {
              // Don't trigger on gift items or free shipping bar changes
              if (!this.isGiftRelatedElement(node)) {
                this.invalidateCache();
                setTimeout(() => this.checkAndManageGifts(), 200);
              }
            }
          });

          // Check if new cart items were added
          mutation.addedNodes.forEach((node) => {
            if (
              node.nodeType === Node.ELEMENT_NODE &&
              ((node.matches &&
                node.matches(
                  ".cart-item, .quick-cart__item, [data-cart-item]",
                )) ||
                (node.querySelector &&
                  node.querySelector(
                    ".cart-item, .quick-cart__item, [data-cart-item]",
                  )))
            ) {
              // Don't trigger on gift items or free shipping bar changes
              if (!this.isGiftRelatedElement(node)) {
                this.invalidateCache();
                setTimeout(() => this.checkAndManageGifts(), 200);
              }
            }
          });
        }
      });
    });

    // Start observing cart containers (but exclude free shipping bar)
    const cartContainers = document.querySelectorAll(
      ".cart, .quick-cart, .cart__items, .quick-cart__items",
    );
    cartContainers.forEach((container) => {
      if (
        container &&
        !container.matches("[data-free-shipping-bar], .free-shipping-bar")
      ) {
        cartItemObserver.observe(container, {
          childList: true,
          subtree: true,
        });
      }
    });

    this.cartItemObserver = cartItemObserver;
  }

  isGiftRelatedElement(element) {
    if (!element || !element.textContent) return false;

    // EXCLUDE free shipping bar - let theme handle this completely
    const isFreeShippingBar =
      element.matches &&
      (element.matches("[data-free-shipping-bar], .free-shipping-bar") ||
        element.closest("[data-free-shipping-bar], .free-shipping-bar"));

    if (isFreeShippingBar) {
      return false; // Don't monitor free shipping bar
    }

    // Only check if it's a gift item
    const isGift =
      element.textContent.includes("Travel Spray") ||
      element.textContent.includes("Room Spray") ||
      (element.querySelector && element.querySelector('[data-gift="true"]'));

    return isGift;
  }

  stopRealTimeCartMonitoring() {
    if (this.realTimeMonitor) {
      clearInterval(this.realTimeMonitor);
      this.realTimeMonitor = null;
    }

    if (this.cartItemObserver) {
      this.cartItemObserver.disconnect();
      this.cartItemObserver = null;
    }
  }

  startCartDrawerMonitoring() {
    if (this.cartDrawerMonitor) {
      return; // Already monitoring
    }

    // Monitor cart drawer when open (reasonable frequency)
    this.cartDrawerMonitor = setInterval(() => {
      if (this.internalOperation || !this.isCartDrawerOpen) return;

      // Monitor cart drawer quantity inputs specifically
      const cartDrawerInputs = document.querySelectorAll(
        ".quick-cart .quantity-input__input, .cart-drawer .quantity-input__input",
      );

      cartDrawerInputs.forEach((input) => {
        const currentValue = parseInt(input.value) || 0;
        const lastValue = parseInt(input.dataset.lastCartDrawerValue) || 0;

        if (currentValue !== lastValue) {
          input.dataset.lastCartDrawerValue = currentValue;

          // Use scheduled check
          this.invalidateCache();
          this.scheduleCheck("cart-drawer");
        }
      });

      // Monitor cart totals
      const cartTotalElements = document.querySelectorAll(
        ".quick-cart__footer-subtotal, .cart-drawer__subtotal",
      );
      cartTotalElements.forEach((element) => {
        const currentText = element.textContent.trim();
        const lastText = element.dataset.lastCartDrawerTotal || "";

        if (currentText !== lastText && currentText.includes("$")) {
          element.dataset.lastCartDrawerTotal = currentText;

          this.invalidateCache();
          this.scheduleCheck("cart-drawer");
        }
      });
    }, 2000); // Every 2 seconds when cart drawer is open
  }

  stopCartDrawerMonitoring() {
    if (this.cartDrawerMonitor) {
      clearInterval(this.cartDrawerMonitor);
      this.cartDrawerMonitor = null;
    }
  }

  debouncedMonitoringCheck(source, delay = 0) {
    const now = Date.now();

    // Prevent too frequent monitoring calls (throttling)
    if (this.lastMonitorTime && now - this.lastMonitorTime < 1000) {
      return;
    }

    if (this.monitoringDebounce) {
      clearTimeout(this.monitoringDebounce);
    }

    this.monitoringDebounce = setTimeout(() => {
      this.lastMonitorTime = Date.now();
      this.scheduleCheck(source);
    }, delay);
  }

  async validateGiftsBeforeCheckout(checkoutButton) {
    try {
      console.log("🔎 Pre-checkout validation starting...");

      // Disable checkout button during validation
      this.disableCheckoutButtons("Validating cart...");

      const cart = await this.getCurrentCart();
      if (!cart) {
        console.log("⚠️ Could not get cart, proceeding with checkout");
        this.enableCheckoutButtons();
        this.proceedWithCheckout(checkoutButton);
        return;
      }

      // Calculate subtotal for gift eligibility
      const subtotal = this.calculateSubtotal(cart);
      const giftsInCart = this.getGiftsInCart(cart);
      const qualifyingTier = this.getQualifyingGiftTier(subtotal);

      console.log(
        `🔎 Pre-checkout check: Subtotal: $${subtotal}, Gifts in cart: ${
          giftsInCart.length
        }, Qualifying tier: ${qualifyingTier ? qualifyingTier.name : "NONE"}`,
      );

      if (qualifyingTier) {
        const isManuallyRemovedCheck = this.isGiftManuallyRemoved(
          qualifyingTier.threshold,
        );
        console.log(
          `🔍 Pre-checkout: Tier ${qualifyingTier.threshold} manually removed: ${isManuallyRemovedCheck}`,
        );
      }

      let needsCartUpdate = false;

      // Check if we need to remove gifts due to insufficient subtotal
      if (subtotal < 130 && giftsInCart.length > 0) {
        console.log(
          "❌ Pre-checkout: Removing gifts due to insufficient subtotal ($130 minimum)",
        );
        this.showCheckoutValidation("Removing ineligible gifts...");

        // Remove all gifts
        await this.updateCartGifts(null);
        needsCartUpdate = true;
      } else if (qualifyingTier) {
        // Check if current gifts match the qualifying tier
        const hasCorrectGifts = this.hasCorrectTierGifts(
          giftsInCart,
          qualifyingTier,
        );

        // Check if user manually removed this tier's gifts
        const isManuallyRemoved = this.isGiftManuallyRemoved(
          qualifyingTier.threshold,
        );

        if (!hasCorrectGifts && !isManuallyRemoved) {
          console.log("🎁 Pre-checkout: Adding eligible gifts");
          this.showCheckoutValidation("Adding your free gifts...");

          await this.updateCartGifts(qualifyingTier);
          needsCartUpdate = true;
        } else if (!hasCorrectGifts && isManuallyRemoved) {
          console.log(
            "✅ Pre-checkout: Respecting user's choice to remove gifts",
          );
          // Show a brief message that they're eligible for gifts but chose to remove them
          this.showBriefGiftReminder(qualifyingTier);

          // Proceed with checkout immediately since we're respecting their choice
          setTimeout(() => {
            this.enableCheckoutButtons();
            console.log(
              "✅ Pre-checkout validation complete - proceeding with user's choice",
            );
            this.proceedWithCheckout(checkoutButton);
          }, 1500); // Give time for the reminder toast to show
          return; // Exit early to prevent the normal flow
        }
      } else if (giftsInCart.length > 0) {
        // No qualifying tier but gifts exist - remove them
        console.log(
          "❌ Pre-checkout: Removing invalid gifts (no qualifying tier)",
        );
        this.showCheckoutValidation("Removing invalid gifts...");

        await this.updateCartGifts(null);
        needsCartUpdate = true;
      }

      if (needsCartUpdate) {
        // Wait for cart to update, then proceed
        setTimeout(() => {
          this.hideCheckoutValidation();
          this.enableCheckoutButtons();
          console.log(
            "✅ Pre-checkout validation complete - proceeding to checkout",
          );
          this.proceedWithCheckout(checkoutButton);
        }, 2000);
      } else {
        console.log("✅ Pre-checkout validation complete - no changes needed");
        this.enableCheckoutButtons();
        this.proceedWithCheckout(checkoutButton);
      }
    } catch (error) {
      console.error("❌ Pre-checkout validation failed:", error);

      // Hide any validation overlay and re-enable buttons
      this.hideCheckoutValidation();
      this.enableCheckoutButtons();

      // Proceed anyway if validation fails
      this.proceedWithCheckout(checkoutButton);
    }
  }

  proceedWithCheckout(checkoutButton) {
    console.log("🚀 Proceeding with checkout...");

    // Force navigate to checkout instead of relying on form action
    // This prevents bouncing back to cart page
    window.location.href = "/checkout";
  }

  // Checkout button management functions
  disableCheckoutButtons(reason = "Processing gifts...") {
    console.log(`🚫 Disabling checkout buttons: ${reason}`);

    // Disable main cart checkout button
    const cartSubmitButton = document.querySelector(".cart__submit");
    if (cartSubmitButton) {
      cartSubmitButton.disabled = true;
      cartSubmitButton.style.opacity = "0.6";
      cartSubmitButton.style.pointerEvents = "none";
      cartSubmitButton.dataset.originalText = cartSubmitButton.textContent;
      cartSubmitButton.textContent = reason;
    }

    // Disable quick cart checkout button
    const quickCartSubmitButton = document.querySelector(".quick-cart__submit");
    if (quickCartSubmitButton) {
      quickCartSubmitButton.disabled = true;
      quickCartSubmitButton.style.opacity = "0.6";
      quickCartSubmitButton.style.pointerEvents = "none";
      quickCartSubmitButton.dataset.originalText =
        quickCartSubmitButton.textContent;
      quickCartSubmitButton.textContent = reason;
    }
  }

  enableCheckoutButtons() {
    console.log("✅ Enabling checkout buttons");

    // Enable main cart checkout button
    const cartSubmitButton = document.querySelector(".cart__submit");
    if (cartSubmitButton) {
      cartSubmitButton.disabled = false;
      cartSubmitButton.style.opacity = "";
      cartSubmitButton.style.pointerEvents = "";

      // Restore original text or provide fallback
      if (cartSubmitButton.dataset.originalText) {
        cartSubmitButton.textContent = cartSubmitButton.dataset.originalText;
        delete cartSubmitButton.dataset.originalText;
      } else if (
        cartSubmitButton.textContent === "Initializing..." ||
        cartSubmitButton.textContent.includes("Processing")
      ) {
        // Fallback if stuck in initialization state
        cartSubmitButton.textContent = "Checkout";
        console.log("🔄 Reset cart button text from stuck state");
      }
    }

    // Enable quick cart checkout button
    const quickCartSubmitButton = document.querySelector(".quick-cart__submit");
    if (quickCartSubmitButton) {
      quickCartSubmitButton.disabled = false;
      quickCartSubmitButton.style.opacity = "";
      quickCartSubmitButton.style.pointerEvents = "";

      // Restore original text or provide fallback
      if (quickCartSubmitButton.dataset.originalText) {
        quickCartSubmitButton.textContent =
          quickCartSubmitButton.dataset.originalText;
        delete quickCartSubmitButton.dataset.originalText;
      } else if (
        quickCartSubmitButton.textContent === "Initializing..." ||
        quickCartSubmitButton.textContent.includes("Processing")
      ) {
        // Fallback if stuck in initialization state
        quickCartSubmitButton.textContent = "Checkout";
        console.log("🔄 Reset quick cart button text from stuck state");
      }
    }
  }

  showCheckoutValidation(message) {
    const overlay = document.createElement("div");
    overlay.id = "gift-validation-overlay";
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10001;
      font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    `;

    overlay.innerHTML = `
      <div style="
        background: white;
        padding: 30px;
        border-radius: 8px;
        text-align: center;
        box-shadow: 0 8px 32px rgba(0,0,0,0.3);
      ">
        <div style="margin-bottom: 15px; font-size: 24px;">🎁</div>
        <div style="font-size: 16px; color: #333; margin-bottom: 10px;">${message}</div>
        <div style="font-size: 14px; color: #666;">Please wait...</div>
      </div>
    `;

    document.body.appendChild(overlay);
  }

  hideCheckoutValidation() {
    const overlay = document.getElementById("gift-validation-overlay");
    if (overlay) {
      overlay.remove();
    }
  }

  showBriefGiftReminder(qualifyingTier) {
    // Show a brief, non-blocking reminder that they're eligible for gifts
    this.showGiftToast(
      `💡 You're eligible for free ${qualifyingTier.name} but chose to remove them`,
      "#6c757d", // Gray color for info
    );

    // Don't do anything else here - let the calling function handle the checkout flow
    console.log("💡 Gift reminder shown - respecting user choice");
  }

  setupFallbackCartMonitoring() {
    console.log(
      "🔧 Setting up fallback cart monitoring with proper throttling...",
    );

    // Monitor cart changes as fallback (reasonable frequency)
    this.fallbackMonitor = setInterval(async () => {
      if (this.isProcessing || this.internalOperation) {
        return;
      }

      try {
        const currentCart = await this.getCurrentCart();
        if (!currentCart) return;

        const currentSubtotal = this.calculateSubtotal(currentCart);
        const currentItemCount = currentCart.item_count;

        // Check if cart has changed since last check
        if (
          this.lastFallbackSubtotal !== currentSubtotal ||
          this.lastFallbackItemCount !== currentItemCount
        ) {
          console.log(
            `🔍 Fallback detected cart change: $${currentSubtotal} (${currentItemCount} items)`,
          );
          this.lastFallbackSubtotal = currentSubtotal;
          this.lastFallbackItemCount = currentItemCount;

          // Trigger gift check
          this.invalidateCache();
          this.scheduleCheck("fallback-monitor");
        }
      } catch (error) {
        console.log("❌ Fallback monitoring error:", error);
      }
    }, 8000); // Every 8 seconds as fallback
  }

  scheduleCheck(type = "normal") {
    if (this.isProcessing) {
      console.log(`⏸️ Skipping ${type} check - already processing`);
      return;
    }

    // Clear existing timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    // Use the new debounced method with appropriate delays (matching working file pattern)
    let debounceTime;
    switch (type) {
      case "quantity":
      case "realtime-quantity":
        debounceTime = 300; // Quick response for quantity changes
        break;
      case "realtime-total":
      case "cart-event":
        debounceTime = 150; // Very fast for cart events
        break;
      case "removal":
        debounceTime = 800; // Increased for removals to let cart settle
        break;
      case "fallback-monitor":
        debounceTime = 100; // Immediate for fallback detections
        break;
      case "dom":
      case "subtotal":
        debounceTime = 800; // Standard for DOM changes
        break;
      default:
        debounceTime = 1500; // Longer standard delay like working file
    }

    console.log(`🗓️ Scheduling ${type} check in ${debounceTime}ms`);
    this.debounceTimeout = setTimeout(() => {
      this.checkAndManageGifts();
    }, debounceTime);
  }

  invalidateCache() {
    this.cartCache = null;
    this.cacheExpiry = 0;
  }

  // Session storage methods for tracking manually removed gifts
  getManuallyRemovedGifts() {
    try {
      const stored = sessionStorage.getItem(this.sessionStorageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn("Failed to read gift removal tracking:", error);
      return {};
    }
  }

  markGiftAsManuallyRemoved(tierThreshold) {
    try {
      const removed = this.getManuallyRemovedGifts();
      removed[tierThreshold] = Date.now();
      sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(removed));
      console.log(
        `🚫 Marked tier ${tierThreshold} as manually removed for this session`,
      );
    } catch (error) {
      console.warn("Failed to save gift removal tracking:", error);
    }
  }

  isGiftManuallyRemoved(tierThreshold) {
    const removed = this.getManuallyRemovedGifts();
    return removed.hasOwnProperty(tierThreshold);
  }

  clearManuallyRemovedGifts() {
    try {
      sessionStorage.removeItem(this.sessionStorageKey);
      console.log("🧹 Cleared manually removed gifts tracking");
    } catch (error) {
      console.warn("Failed to clear gift removal tracking:", error);
    }
  }

  clearLowerTierRemovals(currentTierThreshold) {
    try {
      const removed = this.getManuallyRemovedGifts();
      let hasChanges = false;

      // Remove tracking for any tiers below the current threshold
      for (const tierThreshold in removed) {
        if (parseInt(tierThreshold) < currentTierThreshold) {
          delete removed[tierThreshold];
          hasChanges = true;
          console.log(
            `🧹 Cleared removal tracking for lower tier: ${tierThreshold}`,
          );
        }
      }

      if (hasChanges) {
        sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(removed));
      }
    } catch (error) {
      console.warn("Failed to clear lower tier removal tracking:", error);
    }
  }

  detectManualGiftRemovals(cart, subtotal) {
    console.log("🕵️ Running gift removal detection...");

    // Skip detection if we don't have previous cart data or if this is first run
    if (!this.lastCartSnapshot) {
      console.log("  📸 Creating initial cart snapshot");
      this.lastCartSnapshot = {
        gifts: this.getGiftsInCart(cart),
        subtotal: subtotal,
      };
      return;
    }

    const currentGifts = this.getGiftsInCart(cart);
    const previousGifts = this.lastCartSnapshot.gifts;
    const qualifyingTier = this.getQualifyingGiftTier(subtotal);

    console.log(
      `  Previous gifts: ${previousGifts.length}, Current gifts: ${currentGifts.length}`,
    );
    console.log(
      `  Qualifying tier: ${qualifyingTier ? qualifyingTier.threshold : "NONE"}`,
    );

    // Check if any gifts that should be present are now missing
    if (previousGifts.length > currentGifts.length) {
      console.log(
        "  🔍 Gift count decreased - checking for manual removals...",
      );

      // Find which gifts were removed
      const previousGiftsByThreshold = {};
      previousGifts.forEach((gift) => {
        const threshold = gift.properties?._gift_threshold;
        if (threshold) {
          if (!previousGiftsByThreshold[threshold]) {
            previousGiftsByThreshold[threshold] = [];
          }
          previousGiftsByThreshold[threshold].push(gift);
        }
      });

      const currentGiftsByThreshold = {};
      currentGifts.forEach((gift) => {
        const threshold = gift.properties?._gift_threshold;
        if (threshold) {
          if (!currentGiftsByThreshold[threshold]) {
            currentGiftsByThreshold[threshold] = [];
          }
          currentGiftsByThreshold[threshold].push(gift);
        }
      });

      // Check each tier to see if gifts were removed
      for (const tier of this.giftTiers) {
        const threshold = tier.threshold.toString();
        const previousCount = previousGiftsByThreshold[threshold]?.length || 0;
        const currentCount = currentGiftsByThreshold[threshold]?.length || 0;

        console.log(
          `  Tier ${threshold}: ${previousCount} → ${currentCount} gifts`,
        );

        // If user still qualifies for this tier but gifts are missing, mark as manually removed
        if (subtotal >= tier.threshold && previousCount > currentCount) {
          console.log(
            `🚫 DETECTED manual removal of tier ${threshold} gifts (${previousCount} → ${currentCount})`,
          );
          this.markGiftAsManuallyRemoved(tier.threshold);
        }
      }
    } else {
      console.log("  ✅ No gift count decrease detected");
    }

    // Update snapshot for next comparison
    this.lastCartSnapshot = {
      gifts: currentGifts,
      subtotal: subtotal,
    };
  }

  async getCurrentCart() {
    // Check cache first
    const now = Date.now();
    if (this.cartCache && now < this.cacheExpiry) {
      return this.cartCache;
    }

    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          "Cache-Control": "no-cache",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();

      // Cache the result
      this.cartCache = cart;
      this.cacheExpiry = now + this.cacheValidityMs;
      this.failureCount = 0;

      return cart;
    } catch (error) {
      this.failureCount++;
      console.error("❌ Error fetching cart:", error);

      // Return cached data if available
      return this.cartCache;
    }
  }

  calculateSubtotal(cart) {
    let subtotal = 0;

    console.log("🔢 Calculating subtotal...");
    console.log(
      "🧮 Cart items debug:",
      cart.items.map((item) => ({
        title: item.product_title,
        variant_id: item.variant_id,
        line_price: item.line_price,
        line_price_type: typeof item.line_price,
        quantity: item.quantity,
        price: item.price,
        final_price: item.final_price,
      })),
    );

    for (const item of cart.items) {
      if (this.allGiftVariantIds.has(item.variant_id.toString())) {
        console.log(
          `  Skipping gift: ${item.product_title} (${item.line_price} cents)`,
        );
        continue;
      }

      // Detect inflated line_price values and correct them
      let correctedLinePrice = item.line_price;

      // If line_price seems inflated (more than 1,000,000 cents = $10,000), correct it
      // This handles cases where currency conversion inflates values by 10,000x
      if (item.line_price > 1000000) {
        // Check if dividing by 10,000 gives a reasonable price
        const potentialPrice = item.line_price / 10000;
        if (potentialPrice >= 100 && potentialPrice <= 100000) {
          // Between $1 and $1000
          correctedLinePrice = potentialPrice;
          console.log(
            `  🔧 CORRECTING inflated price: ${item.line_price} → ${correctedLinePrice} cents`,
          );
        }
      }

      const itemValue = correctedLinePrice / 100;
      console.log(
        `  Adding item: ${item.product_title} - ${correctedLinePrice} cents ÷ 100 = $${itemValue}`,
      );
      subtotal += itemValue;
      console.log(`  Running subtotal: $${subtotal}`);
    }

    console.log(`  Final subtotal: $${subtotal} (type: ${typeof subtotal})`);
    return subtotal;
  }

  getGiftsInCart(cart) {
    return cart.items.filter(
      (item) =>
        this.allGiftVariantIds.has(item.variant_id.toString()) &&
        item.properties?._gift === "true",
    );
  }

  hasCorrectTierGifts(giftsInCart, targetTier) {
    if (!targetTier) {
      // No tier qualifies, should have no gifts
      return giftsInCart.length === 0;
    }

    // Check if we have exactly the right gifts for this tier
    const expectedTotalItems = targetTier.items.reduce(
      (total, item) => total + item.quantity,
      0,
    );
    const actualTotalItems = giftsInCart.reduce(
      (total, gift) => total + gift.quantity,
      0,
    );

    if (actualTotalItems !== expectedTotalItems) {
      return false;
    }

    // Check each specific gift matches the target tier exactly
    for (const requiredItem of targetTier.items) {
      const totalQuantityInCart = giftsInCart
        .filter((gift) => gift.variant_id.toString() === requiredItem.variantId)
        .reduce((total, gift) => total + gift.quantity, 0);

      if (totalQuantityInCart !== requiredItem.quantity) {
        return false;
      }
    }

    // CRITICAL: Also check that gifts are from the correct tier threshold
    const giftTiers = giftsInCart.map(
      (gift) => gift.properties?._gift_threshold,
    );
    const targetThreshold = targetTier.threshold.toString();

    for (const giftThreshold of giftTiers) {
      if (giftThreshold !== targetThreshold) {
        return false;
      }
    }
    return true;
  }

  getQualifyingGiftTier(subtotal) {
    // Normalize subtotal to AUD for tier comparison
    const normalizedSubtotal = this.normalizeToAUD(subtotal);

    // DEBUG: Log the comparison values
    console.log("🔍 Gift Tier Debug:");
    console.log("Raw subtotal:", subtotal);
    console.log("Normalized subtotal:", normalizedSubtotal);
    console.log(
      "Available tiers:",
      this.giftTiers.map((t) => t.threshold),
    );

    for (let i = this.giftTiers.length - 1; i >= 0; i--) {
      console.log(
        `Checking tier ${
          this.giftTiers[i].threshold
        }: ${normalizedSubtotal} >= ${this.giftTiers[i].threshold} = ${
          normalizedSubtotal >= this.giftTiers[i].threshold
        }`,
      );
      if (normalizedSubtotal >= this.giftTiers[i].threshold) {
        console.log("✅ Qualifying tier found:", this.giftTiers[i]);
        return this.giftTiers[i];
      }
    }
    console.log("❌ No qualifying tier found");
    return null;
  }

  normalizeToAUD(amount) {
    // Convert amount to AUD using Shopify's currency rate
    const currencyRate = window.Shopify?.currency?.rate || 1;
    const baseCurrency = window.Shopify?.currency?.active || "AUD";

    // If already in AUD or no conversion needed, return as-is
    if (baseCurrency === "AUD" || currencyRate === 1) {
      return amount;
    }

    // FIXED: Convert to AUD (multiply by rate if needed, or use base currency logic)
    // Since Shopify stores everything in base currency (AUD), we should not convert
    // The amount is already in AUD cents, just return it
    return amount;
  }

  getCurrencySymbol() {
    // Get currency symbol for current active currency
    const activeCurrency = window.Shopify?.currency?.active || "AUD";

    const currencySymbols = {
      AUD: "A$",
      USD: "$",
      EUR: "€",
      GBP: "£",
      IDR: "Rp",
      SGD: "S$",
      NZD: "NZ$",
      CAD: "C$",
    };

    return currencySymbols[activeCurrency] || "$";
  }

  async updateCartGifts(giftTier = null) {
    try {
      this.internalOperation = true;

      // Get current cart to build update object
      const cart = await this.getCurrentCart();
      if (!cart) return false;

      const updates = {};
      const giftsInCart = this.getGiftsInCart(cart);

      // Remove all existing gifts
      giftsInCart.forEach((gift) => {
        updates[gift.key] = 0;
      });

      let newItems = [];

      // Add new gifts if tier qualifies
      if (giftTier) {
        const giftProperties = {
          _gift: "true",
          _gift_threshold: giftTier.threshold.toString(),
          _gift_tier: giftTier.name,
          _gift_value: giftTier.value,
        };

        newItems = giftTier.items.map((item) => ({
          id: parseInt(item.variantId),
          quantity: item.quantity,
          properties: giftProperties,
        }));
      } else {
        console.log("🧹 Removing all gifts - no qualifying tier");
      }

      // Perform batch update: remove old gifts first
      if (Object.keys(updates).length > 0) {
        await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates }),
        });

        // Small delay between operations
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      // Add new gifts if any
      if (newItems.length > 0) {
        const addResponse = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: newItems }),
        });

        if (!addResponse.ok) {
          console.error("❌ Failed to add gifts");
          return false;
        }
      }

      // Invalidate cache and trigger cart updates
      this.invalidateCache();

      // CRITICAL: Wait a moment for cart API to complete, then trigger UI updates
      setTimeout(() => {
        this.triggerCartUpdate();

        // Additional immediate refresh for gift additions
        if (giftTier) {
          console.log("🔄 Forcing additional refresh after gift addition...");
          setTimeout(() => {
            // Force theme to re-read cart data
            this.triggerAllCartUpdateMethods();
          }, 200);
        }
      }, 100);

      if (giftTier) {
        this.showGiftNotification("added", giftTier);
      } else {
        this.showGiftNotification("removed");
      }

      return true;
    } catch (error) {
      console.error("❌ Error updating cart gifts:", error);
      return false;
    } finally {
      setTimeout(() => {
        this.internalOperation = false;
      }, 1000);
    }
  }

  triggerCartUpdate() {
    console.log("🔄 Triggering cart update events...");

    // CRITICAL: Trigger events both WITH and WITHOUT source to ensure theme picks them up
    const events = ["cart:updated", "cart:changed", "cart:refresh"];
    events.forEach((eventName) => {
      // First trigger with source (for debugging)
      const eventWithSource = new CustomEvent(eventName, {
        detail: {
          source: "giftManager",
          timestamp: Date.now(),
        },
      });
      document.dispatchEvent(eventWithSource);

      // Then trigger without source (for theme to pick up)
      const eventForTheme = new CustomEvent(eventName, {
        detail: {
          timestamp: Date.now(),
        },
      });
      document.dispatchEvent(eventForTheme);
      window.dispatchEvent(eventForTheme);
    });

    // Update cart drawer/mini-cart if present
    this.updateCartUI();
  }

  updateCartUI() {
    // Only update UI if cart actually changed
    console.log("🔄 Updating cart UI after gift change");
    this.triggerCartUpdate();

    // Force refresh the quick cart with throttling
    this.reloadQuickCart();
  }

  triggerImmediateCartRefresh() {
    // Force fetch fresh cart data and update UI
    fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        // Update cart cache with fresh data
        this.cartCache = cart;
        this.cacheExpiry = Date.now() + this.cacheValidityMs;

        // Update cart count/totals in header immediately
        this.updateCartDisplay(cart);

        // Update gift progress indicator
        this.updateGiftProgress(cart);

        // Force reload cart drawer/mini-cart content
        this.forceCartUIReload(cart);

        // Trigger theme-specific updates
        this.triggerAllCartUpdateMethods();
      })
      .catch((error) => {
        console.error("Cart refresh failed:", error);
        // Still try to trigger updates
        this.triggerAllCartUpdateMethods();
      });
  }

  forceCartUIReload(cart) {
    // Force reload the cart drawer/quick cart HTML
    this.reloadQuickCart();
    this.reloadCartPage();
  }

  reloadQuickCart() {
    // Throttle quick cart reloads to prevent excessive requests
    const now = Date.now();
    if (this.lastQuickCartReload && now - this.lastQuickCartReload < 3000) {
      console.log("⏱️ Throttling quick cart reload - too soon since last call");
      return;
    }
    this.lastQuickCartReload = now;

    // For Stiletto theme quick cart
    const quickCart = document.querySelector(".quick-cart");
    if (quickCart) {
      // Trigger Stiletto's quick cart refresh by simulating cart open/close
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent("quick-cart:close"));
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent("quick-cart:open"));
        }, 100);
      }, 200);
    }

    // Only fetch cart HTML if really needed (throttled)
    fetch("/cart?view=drawer")
      .then((response) => response.text())
      .then((html) => {
        const cartDrawers = document.querySelectorAll(
          ".quick-cart__items, .cart-drawer__items",
        );
        cartDrawers.forEach((drawer) => {
          if (drawer) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const newItems = doc.querySelector(
              ".cart__items, .quick-cart__items",
            );
            if (newItems) {
              drawer.innerHTML = newItems.innerHTML;
              console.log("✅ Refreshed quick cart items (throttled)");
            }
          }
        });
      })
      .catch((error) => console.log("Cart drawer refresh failed:", error));
  }

  reloadCartPage() {
    // If on cart page, reload the items section
    if (window.location.pathname.includes("/cart")) {
      const cartForm = document.querySelector(
        '.cart__form, form[action="/cart"]',
      );
      if (cartForm) {
        fetch("/cart")
          .then((response) => response.text())
          .then((html) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const newCartItems = doc.querySelector(
              ".cart__form-items, .cart__items, .cart-items",
            );
            const currentCartItems = document.querySelector(
              ".cart__form-items, .cart__items, .cart-items",
            );

            if (newCartItems && currentCartItems) {
              currentCartItems.innerHTML = newCartItems.innerHTML;
            }
          })
          .catch((error) => console.log("Cart page refresh failed:", error));
      }
    }
  }

  showGiftToast(message, backgroundColor = "#28a745") {
    // Create a simple toast notification
    const toast = document.createElement("div");
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${backgroundColor};
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, sans-serif;
      font-size: 14px;
      font-weight: 500;
      transform: translateX(400px);
      transition: transform 0.3s ease;
    `;
    toast.textContent = message;
    document.body.appendChild(toast);

    // Animate in
    setTimeout(() => {
      toast.style.transform = "translateX(0)";
    }, 100);

    // Remove after 4 seconds
    setTimeout(() => {
      toast.style.transform = "translateX(400px)";
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 4000);
  }

  updateGiftProgress(cart) {
    const subtotal = this.calculateSubtotal(cart);
    const normalizedSubtotal = this.normalizeToAUD(subtotal);
    const qualifyingTier = this.getQualifyingGiftTier(subtotal);
    const nextTier = this.getNextTier(qualifyingTier);

    // Create or update gift progress message
    let progressMessage = "";
    let progressClass = "";

    if (qualifyingTier) {
      progressMessage = `🎁 Free gift unlocked: ${qualifyingTier.name}`;
      progressClass = "gift-unlocked";
    } else if (nextTier) {
      const remaining = nextTier.threshold - normalizedSubtotal;
      const currencyRate = window.Shopify?.currency?.rate || 1;
      const remainingInCurrentCurrency = remaining * currencyRate;
      const currencySymbol = this.getCurrencySymbol();

      progressMessage = `🎁 Add ${currencySymbol}${remainingInCurrentCurrency.toFixed(
        2,
      )} more for free ${nextTier.name}`;
      progressClass = "gift-progress";
    }

    // Only update if message changed to prevent blinking
    if (progressMessage && progressMessage !== this.lastProgressMessage) {
      this.lastProgressMessage = progressMessage;
      this.displayGiftProgress(progressMessage, progressClass);
    } else if (!progressMessage && this.lastProgressMessage) {
      // Hide progress if no longer applicable
      this.hideGiftProgress();
      this.lastProgressMessage = null;
    }
  }

  displayGiftProgress(message, className) {
    // Only show gift messages in the cart area, not as standalone elements
    let targetElement = document.querySelector(
      "[data-free-shipping-bar], .free-shipping-bar",
    );

    if (targetElement) {
      // Create a separate gift message element under the free shipping bar
      this.addGiftMessageToFreeShippingBar(targetElement, message, className);
    }
    // No standalone gift progress - only in cart area
  }

  hijackFreeShippingMessage(freeShippingBar, message, className) {
    // Alternative: Take over the existing free shipping message
    const existingMessage = freeShippingBar.querySelector(
      ".free-shipping-bar__message",
    );
    if (existingMessage) {
      // Store original content if not already stored
      if (!existingMessage.dataset.originalContent) {
        existingMessage.dataset.originalContent = existingMessage.textContent;
      }

      // Update with gift message
      existingMessage.textContent = message;
      existingMessage.dataset.giftMessage = "true";

      // Style the parent wrapper for gift message
      const wrapper = existingMessage.closest(
        ".inventory-counter__message-wrapper",
      );
      if (wrapper) {
        const backgroundColor =
          className === "gift-unlocked" ? "#d4edda" : "#fff3cd";
        const textColor = className === "gift-unlocked" ? "#155724" : "#856404";
        const borderColor =
          className === "gift-unlocked" ? "#c3e6cb" : "#ffeaa7";

        wrapper.style.cssText = `
          background: ${backgroundColor} !important;
          color: ${textColor} !important;
          border: 1px solid ${borderColor} !important;
          border-radius: 4px !important;
          padding: 8px 12px !important;
          margin: 8px 0 !important;
        `;
        wrapper.dataset.giftMessage = "true";
      }

      // Set up a persistent watcher to prevent overwriting
      this.protectGiftMessage(existingMessage, message, wrapper, className);
    }
  }

  protectGiftMessage(messageElement, giftMessage, wrapper, className) {
    // Clear any existing protection
    if (this.giftMessageProtector) {
      clearInterval(this.giftMessageProtector);
    }

    // Set up a watcher that ensures the gift message stays visible
    this.giftMessageProtector = setInterval(() => {
      if (messageElement && messageElement.textContent !== giftMessage) {
        messageElement.textContent = giftMessage;
        messageElement.dataset.giftMessage = "true";

        if (wrapper) {
          const backgroundColor =
            className === "gift-unlocked" ? "#d4edda" : "#fff3cd";
          const textColor =
            className === "gift-unlocked" ? "#155724" : "#856404";
          const borderColor =
            className === "gift-unlocked" ? "#c3e6cb" : "#ffeaa7";

          wrapper.style.cssText = `
            background: ${backgroundColor} !important;
            color: ${textColor} !important;
            border: 1px solid ${borderColor} !important;
            border-radius: 4px !important;
            padding: 8px 12px !important;
            margin: 8px 0 !important;
          `;
        }
      }
    }, 200); // Check every 200ms
  }

  protectGiftMessageElement(giftElement, message, className) {
    // Clear any existing protection
    if (this.giftElementProtector) {
      clearInterval(this.giftElementProtector);
    }

    // Set up a watcher that ensures the separate gift element stays visible and styled
    this.giftElementProtector = setInterval(() => {
      if (!giftElement.parentNode) {
        // Element was removed, try to recreate it
        const freeShippingBar = document.querySelector(
          "[data-free-shipping-bar], .free-shipping-bar",
        );
        if (freeShippingBar) {
          this.addGiftMessageToFreeShippingBar(
            freeShippingBar,
            message,
            className,
          );
        }
        return;
      }

      // Check if content or styling was changed
      if (giftElement.textContent !== message || !giftElement.style.zIndex) {
        giftElement.textContent = message;

        const backgroundColor =
          className === "gift-unlocked" ? "#d4edda" : "#fff3cd";
        const textColor = className === "gift-unlocked" ? "#155724" : "#856404";
        const borderColor =
          className === "gift-unlocked" ? "#c3e6cb" : "#ffeaa7";

        giftElement.style.cssText = `
          padding: 10px 16px;
          margin: 8px auto;
          max-width: 600px;
          background: ${backgroundColor} !important;
          color: ${textColor} !important;
          border: 2px solid ${borderColor} !important;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          text-align: center;
          display: block !important;
          opacity: 1 !important;
          visibility: visible !important;
          transition: all 0.3s ease;
          position: relative;
          z-index: 10000;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        `;
      }
    }, 300); // Check every 300ms
  }

  addGiftMessageToFreeShippingBar(freeShippingBar, message, className) {
    // Look for existing gift message (search in parent container since we insert after)
    let giftMessage = document.querySelector(".gift-progress-message");

    if (!giftMessage) {
      giftMessage = document.createElement("div");
      giftMessage.className = "gift-progress-message";

      // Insert directly after the free shipping bar container to position it below
      freeShippingBar.insertAdjacentElement("afterend", giftMessage);
    }

    // Update styling based on className
    const backgroundColor =
      className === "gift-unlocked" ? "#d4edda" : "#fff3cd";
    const textColor = className === "gift-unlocked" ? "#155724" : "#856404";

    giftMessage.style.cssText = `
      padding: 10px 16px;
      margin: 8px auto;
      max-width: 600px;
      background: ${backgroundColor} !important;
      color: ${textColor} !important;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 500;
      text-align: center;
      display: block !important;
      opacity: 1 !important;
      visibility: visible !important;
      transition: all 0.3s ease;
      position: relative;
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    giftMessage.textContent = message;
    giftMessage.className = `gift-progress-message ${className}`;

    // Add protection to prevent theme from removing this element
    this.protectGiftMessageElement(giftMessage, message, className);
  }

  cleanupStandaloneElements() {
    // Remove any existing standalone gift elements from the page
    const standaloneElements = document.querySelectorAll(
      ".gift-progress-standalone, .gift-progress",
    );
    standaloneElements.forEach((element) => {
      element.remove();
    });
  }

  hideGiftProgress() {
    // Clear both protection systems
    if (this.giftMessageProtector) {
      clearInterval(this.giftMessageProtector);
      this.giftMessageProtector = null;
    }

    if (this.giftElementProtector) {
      clearInterval(this.giftElementProtector);
      this.giftElementProtector = null;
    }

    // Remove separate gift message element completely
    const giftMessage = document.querySelector(".gift-progress-message");
    if (giftMessage) {
      giftMessage.remove();
    }

    // Also cleanup any standalone elements
    this.cleanupStandaloneElements();
  }

  ensureFreeShippingBarWorks() {
    // Use a more specific approach - wait for DOM to be ready then find elements
    setTimeout(() => {
      const freeShippingBars = document.querySelectorAll(
        "[data-free-shipping-bar], .free-shipping-bar",
      );

      freeShippingBars.forEach((bar) => {
        if (bar.dataset.threshold && bar.dataset.cartTotal) {
          // Check if message element exists before proceeding
          const messageElement = bar.querySelector(
            ".free-shipping-bar__message",
          );
          if (messageElement) {
            // Call the theme's freeShippingBar function if it exists
            if (
              typeof window.theme !== "undefined" &&
              typeof window.theme.freeShippingBar === "function"
            ) {
              window.theme.freeShippingBar(bar);
            } else {
              // Fallback: manually set the message based on theme logic
              this.setFreeShippingMessage(bar);
            }
          } else {
            // Retry after a short delay
            setTimeout(() => this.ensureFreeShippingBarWorks(), 200);
          }
        }
      });
    }, 100);
  }

  setFreeShippingMessage(bar) {
    try {
      let {
        threshold,
        cartTotal,
        freeShippingSuccessMessage,
        freeShippingPendingMessage,
      } = bar.dataset;

      cartTotal = parseInt(cartTotal, 10);
      threshold = Math.round(
        parseInt(threshold, 10) * (window.Shopify?.currency?.rate || 1),
      );
      const thresholdInCents = threshold * 100;

      const message = bar.querySelector(".free-shipping-bar__message");
      if (message) {
        if (cartTotal >= thresholdInCents) {
          bar.classList.add("free-shipping-bar--success");
          const successText =
            freeShippingSuccessMessage ||
            "Your order qualifies for free shipping!";
          message.innerText = successText;
        } else {
          bar.classList.remove("free-shipping-bar--success");
          const remainder = Math.abs(cartTotal - thresholdInCents);
          const formattedAmount = new Intl.NumberFormat("en-AU", {
            style: "currency",
            currency: "AUD",
          }).format(remainder / 100);

          const pendingTemplate =
            freeShippingPendingMessage ||
            "Only {{ remaining_amount }} away from free shipping!";
          const finalMessage = pendingTemplate.replace(
            "{{ remaining_amount }}",
            `<span class="fs-body-bold">${formattedAmount}</span>`,
          );
          message.innerHTML = finalMessage;
        }

        // Also update progress bar
        const progressBar = bar.querySelector(
          ".free-shipping-bar__bar-progress",
        );
        if (progressBar) {
          const progress =
            cartTotal < thresholdInCents
              ? (cartTotal / thresholdInCents) * 100
              : 100;
          progressBar.style.setProperty("--progress-width", `${progress}%`);
        }

        bar.classList.add("free-shipping-bar--loaded");
      } else {
        console.log(
          "  ❌ Message element (.free-shipping-bar__message) not found",
        );
      }
    } catch (error) {
      console.log("⚠️ Error setting free shipping message:", error);
    }
  }

  triggerAllCartUpdateMethods() {
    // Ensure free shipping bar works first
    this.ensureFreeShippingBarWorks();

    // Stiletto theme-specific updates
    if (typeof theme !== "undefined") {
      // Try to trigger Stiletto's quick cart refresh
      if (theme.quickCart?.refresh) theme.quickCart.refresh();
      if (theme.cart?.refresh) theme.cart.refresh();
      if (theme.CartDrawer?.refresh) theme.CartDrawer.refresh();
    }

    // Trigger Stiletto-specific events
    const stilettoEvents = [
      "quick-cart:refresh",
      "cart:refresh",
      "cart:updated",
    ];
    stilettoEvents.forEach((eventName) => {
      document.dispatchEvent(
        new CustomEvent(eventName, {
          detail: { source: "giftManager", timestamp: Date.now() },
        }),
      );
    });

    // Try to manually refresh the quick cart if it exists
    const quickCart = document.querySelector(".quick-cart");
    if (quickCart) {
      // Trigger a re-render by dispatching custom events
      document.dispatchEvent(new CustomEvent("quick-cart:refresh"));
    }

    // Common cart refresh functions
    if (typeof refreshCart === "function") refreshCart();
    if (typeof updateCartDrawer === "function") updateCartDrawer();
    if (typeof updateCart === "function") updateCart();
    if (typeof cartUpdate === "function") cartUpdate();

    // jQuery-based cart updates
    if (typeof $ !== "undefined") {
      $(document).trigger("cart.requestComplete");
      $(document).trigger("cart:updated");
      $(
        ".quick-cart, .cart-drawer, .mini-cart, .cart-popup, .cart-container",
      ).trigger("cart:refresh");
    }

    // Modern event dispatching
    const events = [
      "cart:updated",
      "cart:changed",
      "cart:refresh",
      "shopify:cart:update",
    ];
    events.forEach((eventName) => {
      window.dispatchEvent(
        new CustomEvent(eventName, {
          detail: { source: "giftManager", timestamp: Date.now() },
        }),
      );
      document.dispatchEvent(
        new CustomEvent(eventName, {
          detail: { source: "giftManager", timestamp: Date.now() },
        }),
      );
    });
  }

  updateCartDisplay(cart) {
    // Update cart count in header/nav
    const cartCount = cart.item_count;
    const cartTotal = (cart.total_price / 100).toFixed(2);

    // Common cart count selectors
    const countSelectors = [
      ".cart-count",
      ".cart__count",
      ".cart-item-count",
      ".header-cart-count",
      "[data-cart-count]",
      ".js-cart-count",
    ];

    countSelectors.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((el) => {
        if (el) el.textContent = cartCount;
      });
    });

    // Update cart total displays
    const totalSelectors = [
      ".cart-total",
      ".cart__total",
      ".cart-subtotal",
      "[data-cart-total]",
      ".js-cart-total",
    ];

    totalSelectors.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((el) => {
        if (el) el.textContent = `$${cartTotal}`;
      });
    });
  }

  async checkAndManageGifts() {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      const cart = await this.getCurrentCart();
      if (!cart) {
        return;
      }

      const subtotal = this.calculateSubtotal(cart);

      console.log("🔍 Cart Analysis:");
      console.log(`  Total cart items: ${cart.item_count}`);
      console.log(`  Cart items array length: ${cart.items.length}`);
      console.log(
        `  All gift variant IDs:`,
        Array.from(this.allGiftVariantIds),
      );

      const itemsBreakdown = cart.items.map((item) => ({
        title: item.product_title,
        variant_id: item.variant_id,
        quantity: item.quantity,
        line_price: item.line_price,
        line_price_dollars: item.line_price / 100,
        is_gift: this.allGiftVariantIds.has(item.variant_id.toString()),
      }));

      console.log(`  Cart items breakdown:`, itemsBreakdown);
      console.log(`  Calculated subtotal: $${subtotal}`);

      // Check for manually removed gifts (only if not an internal operation)
      if (!this.internalOperation) {
        this.detectManualGiftRemovals(cart, subtotal);

        // If we just detected a manual removal for the qualifying tier, skip processing
        const qualifyingTierForCheck = this.getQualifyingGiftTier(subtotal);
        if (
          qualifyingTierForCheck &&
          this.isGiftManuallyRemoved(qualifyingTierForCheck.threshold)
        ) {
          console.log(
            `⏹️ Skipping gift processing - tier ${qualifyingTierForCheck.threshold} was just marked as manually removed`,
          );
          return;
        }
      }

      // Check if we need to process this subtotal change
      if (this.lastProcessedSubtotal !== null) {
        const subtotalDiff = Math.abs(subtotal - this.lastProcessedSubtotal);
        const currentTier = this.getQualifyingGiftTier(subtotal);
        const lastTier = this.getQualifyingGiftTier(this.lastProcessedSubtotal);

        // Always process if:
        // 1. Tier changed (crossing threshold boundaries)
        // 2. Significant subtotal change (>$5)
        // 3. Near threshold boundaries (within $10 of any tier)
        const nearThreshold = this.giftTiers.some(
          (tier) => Math.abs(subtotal - tier.threshold) < 10,
        );

        if (currentTier === lastTier && subtotalDiff < 5 && !nearThreshold) {
          return;
        }
      }

      this.lastProcessedSubtotal = subtotal;

      const giftsInCart = this.getGiftsInCart(cart);
      const qualifyingTier = this.getQualifyingGiftTier(subtotal);

      // Log current gifts details
      if (giftsInCart.length > 0) {
        giftsInCart.forEach((gift) => {});
      }

      console.log("🔍 Gift evaluation:");
      console.log(
        `  Qualifying tier: ${qualifyingTier ? qualifyingTier.name : "NONE"}`,
      );
      console.log(`  Current gifts in cart: ${giftsInCart.length}`);
      console.log(`  Cart subtotal: $${subtotal}`);

      if (qualifyingTier) {
        // Check if user upgraded to a higher tier - if so, clear removal tracking for lower tiers
        if (
          this.lastKnownTier &&
          qualifyingTier.threshold > this.lastKnownTier.threshold
        ) {
          console.log(
            `🎉 User upgraded from tier ${this.lastKnownTier.threshold} to ${qualifyingTier.threshold} - clearing lower tier removal tracking`,
          );
          this.clearLowerTierRemovals(qualifyingTier.threshold);
        }
        this.lastKnownTier = qualifyingTier;

        // Check if user manually removed this tier's gifts
        const isManuallyRemoved = this.isGiftManuallyRemoved(
          qualifyingTier.threshold,
        );

        console.log(
          `  Manually removed for tier ${qualifyingTier.threshold}: ${isManuallyRemoved}`,
        );

        const hasCorrectGifts = this.hasCorrectTierGifts(
          giftsInCart,
          qualifyingTier,
        );

        console.log(
          `  Has correct gifts for ${qualifyingTier.name}: ${hasCorrectGifts}`,
        );

        if (!hasCorrectGifts && !isManuallyRemoved) {
          console.log(
            "🎁 Adding/updating gifts for tier:",
            qualifyingTier.name,
          );
          // Use new unified update method
          await this.updateCartGifts(qualifyingTier);
        } else if (!hasCorrectGifts && isManuallyRemoved) {
          console.log(
            "🚫 Skipping gift addition - user manually removed this tier's gifts",
          );
          // Still refresh UI to ensure cart is synced
          this.updateCartUI();
        } else {
          console.log("✅ Gifts already correct for tier");
          // Even if gifts are correct, refresh UI to make sure they're visible
          this.updateCartUI();
        }
      } else if (giftsInCart.length > 0) {
        console.log("🗑️ No qualifying tier but gifts present - REMOVING gifts");
        console.log(
          `  Gifts to remove:`,
          giftsInCart.map((g) => g.product_title),
        );
        // Use new unified update method to remove gifts
        await this.updateCartGifts(null);

        // Force additional UI refresh for removals
        setTimeout(() => {
          console.log("🔄 Additional UI refresh after gift removal...");
          this.triggerAllCartUpdateMethods();
        }, 300);
      } else {
        console.log("✅ No gifts needed and none present");
      }
    } catch (error) {
      console.error("❌ Error in gift management:", error);
    } finally {
      this.isProcessing = false;

      // Mark initialization complete after first successful check
      if (!this.isInitialized) {
        this.markInitializationComplete();
      }
    }
  }

  showGiftNotification(action, giftTier = null) {
    let message;

    if (action === "added" && giftTier) {
      message = `🎁 Free gift added: ${giftTier.name}!`;
      this.showGiftToast(message);
    } else if (action === "removed") {
      message = "🗑️ Free gift removed from cart";
      this.showGiftToast(message, "#dc3545"); // Red color for removal
    }

    // Trigger custom event for external listeners
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("giftManager:notification", {
          detail: { action, giftTier, message },
        }),
      );
    }
  }

  async getGiftStatus() {
    const cart = await this.getCurrentCart();
    if (!cart) return null;

    const subtotal = this.calculateSubtotal(cart);
    const qualifyingTier = this.getQualifyingGiftTier(subtotal);
    const giftsInCart = this.getGiftsInCart(cart);

    return {
      subtotal,
      qualifyingTier,
      giftsInCart,
      nextTier: this.getNextTier(qualifyingTier),
    };
  }

  getNextTier(currentTier) {
    if (!currentTier) return this.giftTiers[0];

    const currentIndex = this.giftTiers.findIndex(
      (tier) => tier.threshold === currentTier.threshold,
    );
    return this.giftTiers[currentIndex + 1] || null;
  }

  destroy() {
    // Clean up timeouts
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    if (this.monitoringDebounce) {
      clearTimeout(this.monitoringDebounce);
    }

    // Clean up monitoring intervals
    if (this.subtotalCheckInterval) {
      clearInterval(this.subtotalCheckInterval);
    }

    if (this.fallbackMonitor) {
      clearInterval(this.fallbackMonitor);
    }

    if (this.giftMessageProtector) {
      clearInterval(this.giftMessageProtector);
    }

    if (this.giftElementProtector) {
      clearInterval(this.giftElementProtector);
    }

    // Clean up observers
    this.stopRealTimeCartMonitoring();
    this.stopCartDrawerMonitoring();
    this.invalidateCache();

    console.log(
      "🧹 Gift manager destroyed - all intervals and observers cleared",
    );
  }

  // Mark initialization as complete and enable checkout if not processing
  markInitializationComplete() {
    if (this.isInitialized) return; // Prevent multiple calls

    console.log("✅ Gift Manager initialization complete");
    this.isInitialized = true;

    // Enable checkout buttons if not currently processing
    if (!this.isProcessing) {
      this.enableCheckoutButtons();
    }
  }
}

// Helper function to manually fix free shipping (for debugging)
window.fixFreeShipping = function () {
  if (window.giftManager) {
    window.giftManager.ensureFreeShippingBarWorks();
  } else {
    console.log("❌ Gift manager not found");
  }
};

// Helper function to debug cart data (for debugging)
window.debugCart = async function () {
  if (window.giftManager) {
    window.giftManager.invalidateCache();

    const cart = await window.giftManager.getCurrentCart();

    if (cart) {
      const subtotal = window.giftManager.calculateSubtotal(cart);
    } else {
      console.log("❌ No cart data found");
    }
  } else {
    console.log("❌ Gift manager not found");
  }
};

// Helper function to debug gift removal tracking
window.debugGiftRemovals = function () {
  if (window.giftManager) {
    const removedGifts = window.giftManager.getManuallyRemovedGifts();
    console.log("🚫 Manually removed gifts:", removedGifts);

    // Show which tiers are blocked
    window.giftManager.giftTiers.forEach((tier) => {
      const isRemoved = window.giftManager.isGiftManuallyRemoved(
        tier.threshold,
      );
      console.log(
        `Tier ${tier.threshold} (${tier.name}): ${isRemoved ? "🚫 BLOCKED" : "✅ ALLOWED"}`,
      );
    });
  } else {
    console.log("❌ Gift manager not found");
  }
};

// Helper function to clear gift removal tracking
window.clearGiftRemovals = function () {
  if (window.giftManager) {
    window.giftManager.clearManuallyRemovedGifts();
    console.log("✅ Cleared all gift removal tracking");
  } else {
    console.log("❌ Gift manager not found");
  }
};

// Helper function to simulate manual gift removal (for testing)
window.simulateGiftRemoval = function (tierThreshold) {
  if (window.giftManager) {
    window.giftManager.markGiftAsManuallyRemoved(tierThreshold);
    console.log(`✅ Simulated manual removal of tier ${tierThreshold} gifts`);
  } else {
    console.log("❌ Gift manager not found");
  }
};

// Auto-initialize when DOM is ready
document.addEventListener("DOMContentLoaded", function () {
  window.giftManager = new OptimizedShopifyGiftManager({
    giftTiers: [
      {
        threshold: 130,
        items: [{ variantId: "42000124936286", quantity: 1 }],
        name: "1x Mystery Gift",
        value: "$50",
      },
      {
        threshold: 200,
        items: [{ variantId: "42000124969054", quantity: 1 }],
        name: "1x Mystery Gift",
        value: "$100",
      },
    ],
  });

  // Initial check with reasonable delay (like working file)
  setTimeout(() => {
    if (window.giftManager) {
      window.giftManager.checkAndManageGifts();
    }
  }, 2000);

  // Safety fallback: if operations take too long, reset internal operation flag
  setTimeout(() => {
    if (window.giftManager && window.giftManager.internalOperation) {
      console.warn(
        "⚠️ Gift Manager operation timeout - resetting internal operation flag as fallback",
      );
      window.giftManager.internalOperation = false;
    }
  }, 10000); // 10 second fallback timeout
});

// Cleanup on page unload
window.addEventListener("beforeunload", () => {
  if (window.giftManager) {
    window.giftManager.destroy();
  }
});

// Export for manual initialization
if (typeof module !== "undefined" && module.exports) {
  module.exports = OptimizedShopifyGiftManager;
}
